import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Currency from 'components/Form/Currency';
import Checkbox from 'components/Form/Checkbox';
import Layout from 'components/Form/PVQLocation/Layouts';
import Branch from 'components/Form/Branch';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonRequired,
  CommonScrollIntoView,
} from 'types/props';
import PVQLocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import { FinancialAssetSeizedItemModel } from 'forms/sections/financial/assetSeized/types';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import { itemsSeizedTypes } from 'constants/enums/financialOptions';
import NotApplicable from 'components/Form/NotApplicable';

interface Props extends Partial<FinancialAssetSeizedItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
}

export default class AssetSeizedItem extends ValidationElement<Props> {
  update = (queue: Partial<FinancialAssetSeizedItemModel>) => {
    this.props.onUpdate({
      SeizureType: this.props.SeizureType,
      SeizureTypeExplain: this.props.SeizureTypeExplain,
      Why: this.props.Why,
      When: this.props.When,
      WhenEstimated: this.props.WhenEstimated,
      AmountOwed: this.props.AmountOwed,
      AmountOwedEstimated: this.props.AmountOwedEstimated,
      Current: this.props.Current,
      WhenResolved: this.props.WhenResolved,
      ActionsTaken: this.props.ActionsTaken,
      ActionsTakenNone: this.props.ActionsTakenNone,
      ActionsTakenReason: this.props.ActionsTakenReason,
      EntityType: this.props.EntityType,
      EntityTypeExplain: this.props.EntityTypeExplain,
      EntityName: this.props.EntityName,
      PrivateName: this.props.PrivateName,
      PropertyLocation: this.props.PropertyLocation,
      FirstPastDue: this.props.FirstPastDue,
      NumberMissed: this.props.NumberMissed,
      CourtName: this.props.CourtName,
      CourtNameNotApplicable: this.props.CourtNameNotApplicable,
      CourtLocation: this.props.CourtLocation,
      OrderNum: this.props.OrderNum,
      OrderNumIdk: this.props.OrderNumIdk,
      ...queue,
    });
  };

  updateSeizureType = (
    values: FinancialAssetSeizedItemModel['SeizureType']
  ) => {
    this.update({
      SeizureType: values,
    });
  };

  updateSeizureTypeExplain = (
    values: FinancialAssetSeizedItemModel['SeizureTypeExplain']
  ) => {
    this.update({
      SeizureTypeExplain: values,
    });
  };

  updateWhy = (
    values: FinancialAssetSeizedItemModel['Why']
  ) => {
    this.update({
      Why: values,
    });
  };

  updateWhen = (
    values: FinancialAssetSeizedItemModel['When']
  ) => {
    this.update({
      When: values,
    });
  };

  updateWhenEstimated = (
    values: FinancialAssetSeizedItemModel['WhenEstimated']
  ) => {
    this.update({
      WhenEstimated: values,
    });
  };

  updateAmountOwed = (
    values: FinancialAssetSeizedItemModel['AmountOwed']
  ) => {
    this.update({
      AmountOwed: values,
    });
  };

  updateAmountOwedEstimated = (
    values: FinancialAssetSeizedItemModel['AmountOwedEstimated']
  ) => {
    this.update({
      AmountOwedEstimated: values,
    });
  };

  updateCurrent = (
    values: FinancialAssetSeizedItemModel['Current']
  ) => {
    this.update({
      Current: values,
    });
  };

  updateWhenResolved = (
    values: FinancialAssetSeizedItemModel['WhenResolved']
  ) => {
    this.update({
      WhenResolved: values,
    });
  };

  updateEntityType = (
    values: FinancialAssetSeizedItemModel['EntityType']
  ) => {
    this.update({
      EntityType: values,
    });
  };

  updateEntityTypeExplain = (
    values: FinancialAssetSeizedItemModel['EntityTypeExplain']
  ) => {
    this.update({
      EntityTypeExplain: values,
    });
  };

  updateEntityName = (
    values: FinancialAssetSeizedItemModel['EntityName']
  ) => {
    this.update({
      EntityName: values,
    });
  };

  updatePrivateName = (
    values: FinancialAssetSeizedItemModel['PrivateName']
  ) => {
    this.update({
      PrivateName: values,
    });
  };

  updatePropertyLocation = (
    values: FinancialAssetSeizedItemModel['PropertyLocation']
  ) => {
    this.update({
      PropertyLocation: values,
    });
  };

  updateFirstPastDue = (
    values: FinancialAssetSeizedItemModel['FirstPastDue']
  ) => {
    this.update({
      FirstPastDue: values,
    });
  };

  updateNumberMissed = (
    values: FinancialAssetSeizedItemModel['NumberMissed']
  ) => {
    this.update({
      NumberMissed: values,
    });
  };

  updateCourtName = (
    values: FinancialAssetSeizedItemModel['CourtName']
  ) => {
    this.update({
      CourtName: values,
    });
  };

  updateCourtNameNotApplicable = (
    values: FinancialAssetSeizedItemModel['CourtNameNotApplicable']
  ) => {
    this.update({
      CourtNameNotApplicable: values,
    });
  };

  updateCourtLocation = (
    values: FinancialAssetSeizedItemModel['CourtLocation']
  ) => {
    this.update({
      CourtLocation: values,
    });
  };

  updateOrderNum = (
    values: FinancialAssetSeizedItemModel['OrderNum']
  ) => {
    this.update({
      OrderNum: values,
    });
  };

  updateOrderNumIdk = (
    values: FinancialAssetSeizedItemModel['OrderNumIdk']
  ) => {
    this.update({
      OrderNumIdk: values,
    });
  };

  updateActionsTaken = (values: FinancialAssetSeizedItemModel['ActionsTaken']) => {
    this.update({
      ActionsTaken: values,
    });
  };

  updateActionsTakenNone = (values: FinancialAssetSeizedItemModel['ActionsTakenNone']) => {
    this.update({
      ActionsTakenNone: values,
    });
  };

  updateActionsTakenReason = (values: FinancialAssetSeizedItemModel['ActionsTakenReason']) => {
    this.update({
      ActionsTakenReason: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;

    return (
      <div className="assetseized-item">
        <Field
          title={i18n.t('financial.pvq.assetSeized.branch.ifYes.combobox.seizureType')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.SeizureType`}
        >
          <RadioGroup
            {...this.props.SeizureType}
            className="option-list option-list-vertical"
            selectedValue={(this.props.SeizureType || {}).value}
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.SeizureType`}
          >
            {itemsSeizedTypes.map((seizureType) => (
              <Radio
                key={seizureType}
                label={seizureType}
                value={seizureType}
                onUpdate={this.updateSeizureType}
              />
            ))}
          </RadioGroup>
        </Field>
        {this.props.SeizureType?.value === 'Other assets' && (
          <Field
            title={i18n.t('financial.pvq.assetSeized.branch.ifYes.combobox.ifOtherTextArea')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.SeizureTypeExplain`}
          >
            <Textarea
              {...this.props.SeizureTypeExplain}
              attribute={`${attribute}.SeizureTypeExplain`}
              name="seizuretypeexplain"
              onUpdate={this.updateSeizureTypeExplain}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.pvq.assetSeized.branch.ifYes.whyTextArea')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Why`}
        >
          <Textarea
            {...this.props.Why}
            attribute={`${attribute}.Why`}
            name="why"
            onUpdate={this.updateWhy}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.assetSeized.branch.ifYes.whenDateEstimated')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.When`}
          exactAttribute
        >
          <DateControl
            {...this.props.When}
            attribute={`${attribute}.When`}
            name="when"
            onUpdate={this.updateWhen}
            onError={this.props.onError}
            hideDay
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            showEstimated={false}
          />
          <Checkbox
            name="whenestimated"
            label={i18n.t('financial.pvq.shared.estimated')}
            {...this.props.WhenEstimated}
            onUpdate={this.updateWhenEstimated}
            onError={this.props.onError}
            attribute={`${this.props.attribute}.WhenEstimated`}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.assetSeized.branch.ifYes.amountOwedTextEstimated')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.AmountOwed`}
        >
          <Currency
            {...this.props.AmountOwed}
            maxLength="10"
            attribute={`${attribute}.AmountOwed`}
            name="amountpastdue"
            onUpdate={this.updateAmountOwed}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
          <Checkbox
            name="amountpastdueestimated"
            label={i18n.t('financial.pvq.shared.estimated')}
            {...this.props.AmountOwedEstimated}
            onUpdate={this.updateAmountOwedEstimated}
            onError={this.props.onError}
            attribute={`${this.props.attribute}.AmountOwedEstimated`}
          />
        </Field>

        <Branch
          {...this.props.Current}
          name="current"
          label={i18n.t('financial.pvq.assetSeized.branch.ifYes.branch.title')}
          labelSize="h4"
          className="assetseized-current-branch"
          onUpdate={this.updateCurrent}
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
          onError={this.handleError}
          attribute={`${attribute}.Current`}
        />

        {this.props.Current?.value === 'No' && (
          <Field
            title={i18n.t('financial.pvq.assetSeized.branch.ifYes.branch.ifNo.whenDateEstimated')}
            titleSize="h4"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.WhenResolved`}
          >
            <DateControl
              {...this.props.WhenResolved}
              attribute={`${attribute}.WhenResolved`}
              name="whenresolved"
              onUpdate={this.updateWhenResolved}
              onError={this.props.onError}
              hideDay
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              showEstimated={true}
            />
          </Field>
        )}

        {this.props.Current?.value === 'Yes' && (
          <Field
            title={i18n.t('financial.pvq.assetSeized.branch.ifYes.branch.ifYes.combobox.actionsTaken')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ActionsTaken`}
          >
            <NotApplicable
              {...this.props.ActionsTakenNone}
              label={i18n.t('financial.pvq.shared.none')}
              attribute={`${attribute}.ActionsTakenNone`}
              onUpdate={this.updateActionsTakenNone}
              scrollIntoView={this.props.scrollIntoView}
              required={this.props.required}
            >
              <Textarea
                {...this.props.ActionsTaken}
                attribute={`${attribute}.ActionsTaken`}
                scrollIntoView={this.props.scrollIntoView}
                onError={this.props.onError}
                onUpdate={this.updateActionsTaken}
                required={this.props.required}
              />
            </NotApplicable>
          </Field>
        )}

        {this.props.ActionsTakenNone?.applicable === false && this.props.Current?.value === 'Yes' && (
          <Field
            title={i18n.t('financial.pvq.assetSeized.branch.ifYes.branch.ifYes.combobox.ifNone')}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ActionsTaken`}
          >
            <Textarea
              {...this.props.ActionsTakenReason}
              attribute={`${attribute}.ActionsTaken`}
              scrollIntoView={this.props.scrollIntoView}
              onUpdate={this.updateActionsTakenReason}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        <Field
          title={i18n.t('financial.pvq.assetSeized.branch.ifYes.courtNameTextNotApplicable')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtName`}
        >
          <NotApplicable
            {...this.props.CourtNameNotApplicable}
            label={i18n.t('financial.pvq.shared.na')}
            onUpdate={this.updateCourtNameNotApplicable}
            attribute={`${attribute}.CourtNameNotApplicable`}
          >
            <Text
              {...this.props.CourtName}
              attribute={`${attribute}.CourtName`}
              name="courtname"
              onUpdate={this.updateCourtName}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('financial.pvq.assetSeized.branch.ifYes.courtLocation')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.CourtLocation`}
        >
          <PVQLocationUnknown
            {...this.props.CourtLocation}
            attribute={`${attribute}.CourtLocation`}
            name="courtlocation"
            layout={Layout.US_STREET_CITY_STATE_ZIP}
            onUpdate={this.updateCourtLocation}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </Field>

        <Field
          title={i18n.t('financial.pvq.assetSeized.branch.ifYes.orderNumTextIdk')}
          titleSize="h4"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.OrderNum`}
        >
          <NotApplicable
            {...this.props.OrderNumIdk}
            name="ordernumidk"
            label={i18n.t('financial.pvq.shared.idk')}
            onUpdate={this.updateOrderNumIdk}
            onError={this.props.onError}
            attribute={`${attribute}.OrderNumIdk`}
          >
            <Text
              {...this.props.OrderNum}
              attribute={`${attribute}.OrderNum`}
              name="ordernum"
              onUpdate={this.updateOrderNum}
              onError={this.props.onError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </NotApplicable>
        </Field>
      </div>
    );
  }
}
AssetSeizedItem.defaultProps = {
  onUpdate: (queue) => { },
  CourtNameNotApplicable: { applicable: true },
  OrderNumIdk: { applicable: true },
  onError: (value, arr) => arr,
};
